import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuctionCard from "@/components/AuctionCard";
import { toast } from "sonner";
import {
  Bell, Heart, Package, Trophy, Gavel, ShieldCheck, ShieldWarning,
  Storefront, ArrowsLeftRight, Users, ChatCircleText, Receipt, ChartLine
} from "@phosphor-icons/react";

const TABS = [
  { id: "listings", label: "İlanlarım", icon: Package },
  { id: "bids", label: "Tekliflerim", icon: Gavel },
  { id: "offers", label: "Fiyat/Takas", icon: ArrowsLeftRight },
  { id: "won", label: "Kazandıklarım", icon: Trophy },
  { id: "favorites", label: "Favorilerim", icon: Heart },
  { id: "follows", label: "Takip Ettiklerim", icon: Users },
  { id: "notifications", label: "Bildirimler", icon: Bell },
  { id: "messages", label: "Mesajlarım", icon: ChatCircleText },
  { id: "purchases", label: "Siparişlerim", icon: Receipt },
  { id: "report", label: "Haftalık Rapor", icon: ChartLine },
];

export default function Dashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const t = params.get("tab");
    return TABS.find((x) => x.id === t) ? t : "listings";
  });
  const [data, setData] = useState(null);
  const [loadingTab, setLoadingTab] = useState(false);

  // sync tab -> URL for shareable links
  useEffect(() => {
    if (params.get("tab") !== tab) {
      setParams((p) => { p.set("tab", tab); return p; }, { replace: true });
    }
    // eslint-disable-next-line
  }, [tab]);

  useEffect(() => {
    if (!loading && !user) nav("/giris");
  }, [loading, user, nav]);

  const loadTab = async (t) => {
    setLoadingTab(true);
    try {
      if (t === "offers") {
        const { data } = await api.get("/me/offers");
        setData(data);
      } else if (t === "messages") {
        const { data } = await api.get("/messages/conversations");
        setData(data);
      } else if (t === "purchases") {
        const { data } = await api.get("/me/orders");
        setData(data);
      } else if (t === "report") {
        const { data } = await api.get("/seller/weekly-report");
        setData(data);
      } else {
        const path = { listings: "/me/listings", bids: "/me/bids", won: "/me/won",
          favorites: "/me/favorites", follows: "/me/follows", notifications: "/me/notifications" }[t];
        const { data } = await api.get(path);
        setData(data);
      }
    } finally { setLoadingTab(false); }
  };

  useEffect(() => { if (user) loadTab(tab); }, [tab, user]);

  const readNotification = async (id) => {
    await api.patch(`/notifications/${id}/read`);
    setData((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };
  const markAllRead = async () => {
    await api.post("/notifications/mark-all-read", {});
    setData((prev) => prev.map((n) => ({ ...n, read: true })));
    toast.success("Tüm bildirimler okundu olarak işaretlendi");
  };

  const reloadMessages = useCallback(async () => {
    const { data } = await api.get("/messages/conversations");
    setData(data);
  }, []);

  const acceptOffer = async (oid) => {
    await api.post(`/offers/${oid}/accept`);
    toast.success("Teklif kabul edildi");
    loadTab("offers");
  };
  const rejectOffer = async (oid) => {
    await api.post(`/offers/${oid}/reject`);
    toast("Teklif reddedildi");
    loadTab("offers");
  };

  if (!user) return null;

  return (
    <div className="max-w-[1280px] mx-auto px-6 py-12" data-testid="dashboard-page">
      <div className="pb-10 mb-10 border-b divider flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-3">Panelim</p>
          <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight text-slate-900">Merhaba, {user.name.split(" ")[0]}</h1>
          <div className="flex items-center gap-3 mt-4 flex-wrap text-sm">
            {user.is_verified ? (
              <span className="inline-flex items-center gap-1.5 text-emerald-800 font-medium text-[11px] uppercase tracking-[0.24em]" data-testid="verified-badge">
                <ShieldCheck size={13} weight="fill" /> Doğrulanmış
              </span>
            ) : (
              <Link to="/dogrula" className="inline-flex items-center gap-1.5 text-[#B54037] font-medium text-[11px] uppercase tracking-[0.24em] hover:underline" data-testid="verify-link">
                <ShieldWarning size={13} weight="fill" /> Doğrulanmamış — Kimlik doğrula
              </Link>
            )}
            {user.role === "corporate" && (
              <>
                <span className="text-slate-300">·</span>
                <Link to={user.store ? `/magaza/${user.store.slug}` : "/magazam/ayarlar"} className="text-[11px] uppercase tracking-[0.24em] font-medium text-slate-700 hover:text-slate-900 flex items-center gap-1" data-testid="store-link">
                  <Storefront size={13} weight="regular" /> {user.store ? "Mağazam" : "Mağaza kur"}
                </Link>
              </>
            )}
            {user.review_count > 0 && (
              <span className="text-slate-500 text-xs">⭐ <span className="font-mono-tight text-slate-900">{user.seller_rating.toFixed(1)}</span> ({user.review_count})</span>
            )}
          </div>
        </div>
        <Link to="/ilan-ver" className="bg-slate-900 text-white px-6 py-3 rounded-lg font-medium text-sm hover:bg-slate-800 transition-colors self-start sm:self-end" data-testid="dashboard-create-btn">
          Yeni ilan ver
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <aside className="md:col-span-3">
          <nav className="flex md:flex-col gap-1 overflow-x-auto">
            {TABS.map((t) => (
              <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`flex items-center gap-3 text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${tab === t.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                <t.icon size={15} weight="regular" /> {t.label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="md:col-span-9" data-testid="dashboard-content">
          {loadingTab ? <p className="text-slate-400">Yükleniyor…</p> :
           tab === "notifications" ? renderNotifications(data || [], readNotification, markAllRead) :
           tab === "offers" ? renderOffers(data || {incoming:[], outgoing:[]}, acceptOffer, rejectOffer) :
           tab === "follows" ? renderFollows(data || []) :
           tab === "messages" ? <MessagesTab conversations={data || []} onRefresh={reloadMessages} /> :
           tab === "purchases" ? renderPurchases(data || []) :
           tab === "report" ? renderReport(data) :
           (data && data.length === 0) ? (
             <Empty text={{
               listings: "Henüz bir ilanınız yok.", bids: "Henüz teklif vermediniz.",
               won: "Henüz kazandığınız ilan yok.", favorites: "Favoriniz yok.",
             }[tab]} />
           ) : (
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
               {(data || []).map((a) => <AuctionCard key={a.id} auction={a} />)}
             </div>
           )}
        </div>
      </div>
    </div>
  );
}

function renderNotifications(data, read, markAll) {
  if (!data.length) return <Empty text="Bildiriminiz yok." />;
  const hasUnread = data.some((n) => !n.read);
  return (
    <div className="space-y-2" data-testid="notifications-list">
      {hasUnread && (
        <div className="flex justify-end mb-2">
          <button onClick={markAll} data-testid="notif-mark-all-read"
            className="text-xs font-medium text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors">
            Tümünü okundu yap
          </button>
        </div>
      )}
      {data.map((n) => (
        <div key={n.id} data-testid={`notif-${n.id}`}
          className={`p-4 rounded-xl flex items-start gap-3 ${n.read ? "bg-white" : "bg-slate-50"}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-none ${n.read ? "bg-slate-100 text-slate-400" : "bg-slate-900 text-white"}`}>
            <Bell size={13} weight="regular" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-slate-800">{n.message}</p>
            <p className="text-[11px] text-slate-400 mt-1 font-mono-tight">{new Date(n.created_at).toLocaleString("tr-TR")}</p>
          </div>
          <div className="flex gap-2 items-center">
            {n.auction_id && <Link to={`/mezat/${n.auction_id}`} className="text-xs font-medium text-slate-600 hover:text-slate-900 underline">Aç</Link>}
            {!n.read && <button onClick={() => read(n.id)} className="text-xs font-medium text-slate-400 hover:text-slate-900">Okundu</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function renderOffers(data, accept, reject) {
  const { incoming, outgoing } = data;
  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-3">Gelen Teklifler</h3>
        {incoming.length === 0 ? <Empty text="Henüz gelen teklif yok." /> : (
          <div className="space-y-3">
            {incoming.map((o) => (
              <div key={o.id} className="border border-stone-200 bg-white p-4 flex items-start gap-4 flex-wrap" data-testid={`offer-in-${o.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">{o.type === "trade" ? "Takas" : "Fiyat"} · <Link to={`/mezat/${o.listing_id}`} className="underline">{o.listing_title}</Link></p>
                  <p className="text-sm font-bold">{o.buyer_name}</p>
                  {o.type === "price" && <p className="font-mono-tight text-xl font-black">₺{Number(o.amount).toLocaleString("tr-TR")}</p>}
                  {o.type === "trade" && <p className="text-sm">Karşılığında: <span className="font-bold">{o.trade_listing_title}</span></p>}
                  {o.message && <p className="text-xs text-stone-600 mt-1 italic">&quot;{o.message}&quot;</p>}
                </div>
                <div className="flex gap-2 items-center">
                  {o.status === "pending" ? (
                    <>
                      <button onClick={() => accept(o.id)} className="bg-green-600 text-white px-4 py-2 font-bold text-sm hover:bg-green-700" data-testid={`offer-accept-${o.id}`}>Kabul</button>
                      <button onClick={() => reject(o.id)} className="border border-stone-300 px-4 py-2 font-bold text-sm hover:border-red-600 hover:text-red-600" data-testid={`offer-reject-${o.id}`}>Reddet</button>
                    </>
                  ) : (
                    <span className={`text-xs uppercase tracking-widest font-bold ${o.status === "accepted" ? "text-green-700" : "text-stone-400"}`}>{o.status === "accepted" ? "Kabul Edildi" : "Reddedildi"}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-3">Gönderdiğim Teklifler</h3>
        {outgoing.length === 0 ? <Empty text="Henüz teklif göndermediniz." /> : (
          <div className="space-y-3">
            {outgoing.map((o) => (
              <div key={o.id} className="border border-stone-200 bg-white p-4 flex items-center gap-4" data-testid={`offer-out-${o.id}`}>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">{o.type === "trade" ? "Takas" : "Fiyat"} · <Link to={`/mezat/${o.listing_id}`} className="underline">{o.listing_title}</Link></p>
                  {o.type === "price" && <p className="font-mono-tight font-bold">₺{Number(o.amount).toLocaleString("tr-TR")}</p>}
                  {o.type === "trade" && <p className="text-sm">Karşılığında: {o.trade_listing_title}</p>}
                </div>
                <span className={`text-xs uppercase tracking-widest font-bold ${o.status === "accepted" ? "text-green-700" : o.status === "rejected" ? "text-red-700" : "text-yellow-700"}`}>
                  {o.status === "pending" ? "Bekliyor" : o.status === "accepted" ? "Kabul Edildi" : "Reddedildi"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function renderFollows(data) {
  if (!data.length) return <Empty text="Henüz mağaza takip etmiyorsunuz." />;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {data.map((u) => (
        <Link key={u.user_id} to={`/magaza/${u.store?.slug}`} className="border border-stone-200 bg-white p-4 hover:border-stone-900 transition-colors flex items-center gap-3">
          <div className="w-12 h-12 bg-stone-950 text-white flex items-center justify-center"><Storefront size={20} weight="bold" /></div>
          <div>
            <p className="font-bold">{u.store?.name}</p>
            <p className="text-xs text-stone-500">{u.store?.followers_count || 0} takipçi</p>
          </div>
        </Link>
      ))}
    </div>
  );
}

function Empty({ text }) {
  return <div className="py-16 text-center text-slate-400 text-sm">{text}</div>;
}

// ---------- Messages Tab ----------
function MessagesTab({ conversations, onRefresh }) {
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const open = async (c) => {
    setActive(c);
    const { data } = await api.get(`/messages/conversations/${c.id}`);
    setDetail(data);
    onRefresh();  // clear unread badge in list
  };

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/messages/conversations/${active.id}/reply`, { text: reply });
      setReply("");
      const { data } = await api.get(`/messages/conversations/${active.id}`);
      setDetail(data);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mesaj gönderilemedi");
    } finally { setSending(false); }
  };

  if (!conversations.length) return <Empty text="Henüz mesajınız yok. İlan detay sayfasında satıcıya mesaj göndererek başlayabilirsiniz." />;

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="messages-tab">
      <div className="md:col-span-4 space-y-1 max-h-[600px] overflow-y-auto pr-2">
        {conversations.map((c) => (
          <button
            key={c.id}
            data-testid={`conv-${c.id}`}
            onClick={() => open(c)}
            className={`w-full text-left p-3 rounded-xl transition-all flex gap-3 ${active?.id === c.id ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`}
          >
            <div className={`flex-1 min-w-0 ${active?.id === c.id ? "text-white" : "text-slate-800"}`}>
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-sm font-medium truncate">{c.role === "seller" ? c.buyer_name : c.seller_name}</p>
                {c.unread > 0 && <span className="text-[9px] uppercase tracking-[0.2em] font-medium bg-[#B54037] text-white rounded-full px-1.5 py-0.5">{c.unread}</span>}
              </div>
              <p className={`text-xs truncate mt-0.5 ${active?.id === c.id ? "text-slate-300" : "text-slate-500"}`}>{c.listing_title}</p>
              <p className={`text-xs truncate mt-1 ${active?.id === c.id ? "text-slate-400" : "text-slate-400"}`}>{c.last_message_preview}</p>
              {c.risk_flags?.length > 0 && (
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#B54037] mt-1 font-medium">⚠ Riskli</p>
              )}
            </div>
          </button>
        ))}
      </div>

      <div className="md:col-span-8" data-testid="conv-detail">
        {!detail ? (
          <div className="py-24 text-center text-slate-400 text-sm">Bir konuşma seçin.</div>
        ) : (
          <div className="flex flex-col rounded-xl bg-slate-50" style={{ maxHeight: 600 }}>
            <div className="p-4 border-b divider">
              <Link to={`/mezat/${detail.conversation.listing_id}`} className="text-sm font-medium text-slate-900 hover:underline">
                {detail.conversation.listing_title}
              </Link>
              <p className="text-xs text-slate-500 mt-0.5">
                {detail.conversation.role === "seller" ? `Alıcı: ${detail.conversation.buyer_name}` : `Satıcı: ${detail.conversation.seller_name}`}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {detail.messages.map((m) => {
                const mine = m.sender_id !== (detail.conversation.role === "buyer" ? detail.conversation.seller_id : detail.conversation.buyer_id);
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? "bg-slate-900 text-white" : "bg-white text-slate-900"}`}>
                      <p className="text-sm">{m.text}</p>
                      <p className={`text-[10px] mt-1 font-mono-tight ${mine ? "text-slate-300" : "text-slate-400"}`}>
                        {new Date(m.created_at).toLocaleTimeString("tr-TR", {hour:"2-digit", minute:"2-digit"})}
                      </p>
                      {m.risk_flags?.length > 0 && (
                        <p className="text-[10px] uppercase tracking-[0.2em] text-amber-400 mt-1">⚠ Riskli içerik</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={send} className="p-3 border-t divider bg-white flex gap-2 rounded-b-xl">
              <input value={reply} onChange={(e) => setReply(e.target.value)} disabled={sending}
                placeholder="Mesajınızı yazın... (telefon/IBAN paylaşmayın)"
                data-testid="conv-reply-input"
                className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 focus:outline-none focus:border-slate-900 text-sm" />
              <button type="submit" disabled={sending || !reply.trim()} data-testid="conv-reply-send"
                className="bg-slate-900 text-white px-5 py-2.5 rounded-lg font-medium text-sm hover:bg-slate-800 transition-colors disabled:opacity-60">
                Gönder
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Purchases Tab ----------
function renderPurchases(orders) {
  if (!orders.length) return <Empty text="Henüz siparişiniz yok." />;
  return (
    <div className="space-y-2" data-testid="purchases-list">
      {orders.map((o) => (
        <div key={o.id} data-testid={`order-${o.id}`} className="p-4 rounded-xl bg-white flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-400 mb-1">
              {o.role === "buyer" ? "Satın alma" : "Satış"} · <Link to={`/mezat/${o.listing_id}`} className="text-slate-600 hover:underline">{o.listing_title}</Link>
            </p>
            <p className="font-mono-tight text-xl font-medium text-slate-900">
              ₺{Number(o.amount).toLocaleString("tr-TR")}
              {o.role === "seller" && <span className="text-sm text-slate-500 ml-2">(size: ₺{Number(o.seller_payout).toLocaleString("tr-TR")})</span>}
            </p>
            <div className="flex gap-1.5 mt-2 flex-wrap text-[10px] uppercase tracking-[0.2em] font-medium">
              <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">{o.status}</span>
              <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">Havuz: {o.escrow_status}</span>
              {o.role === "seller" && <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">Ödeme: {o.payout_status}</span>}
            </div>
          </div>
          <p className="text-xs text-slate-400 font-mono-tight">{new Date(o.created_at).toLocaleDateString("tr-TR")}</p>
        </div>
      ))}
    </div>
  );
}

// ---------- Weekly Report Tab ----------
function openReportPreview() {
  api.get("/seller/weekly-report/preview").then(({ data }) => {
    const blob = new Blob([data], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
    // revoke after some time
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }).catch(() => toast.error("Önizleme yüklenemedi"));
}

function renderReport(data) {
  if (!data) return <p className="text-slate-400">Yükleniyor…</p>;
  const c = data.counters;
  const cards = [
    { label: "Aktif ilan", value: c.listings_active, sub: `${c.listings_total} toplam` },
    { label: "Teklif", value: c.bids, sub: "son 7 gün" },
    { label: "Favori", value: c.favorites_added, sub: "son 7 gün" },
    { label: "Mesaj", value: c.messages_received, sub: "size gelen" },
    { label: "Sipariş", value: c.orders, sub: "tamamlanan" },
    { label: "Ciro (satış)", value: `₺${Number(c.sales_revenue).toLocaleString("tr-TR")}`, sub: `Net: ₺${Number(c.seller_payout_estimated).toLocaleString("tr-TR")}` },
    { label: "Tahmini görüntülenme", value: c.views_estimated, sub: "" },
  ];
  return (
    <div className="space-y-8" data-testid="weekly-report">
      <SellerPayoutCard />
      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
          <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400">Son 7 gün</p>
          <button
            type="button"
            onClick={openReportPreview}
            data-testid="weekly-report-preview-btn"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900 bg-white border border-slate-200 hover:border-slate-900 px-3 py-1.5 rounded-lg transition-colors"
          >
            📧 E-posta önizle
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {cards.map((c) => (
            <div key={c.label} className="p-5 rounded-xl bg-slate-50">
              <p className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-400 mb-2">{c.label}</p>
              <p className="font-mono-tight text-2xl font-medium text-slate-900 tracking-tight">{c.value}</p>
              {c.sub && <p className="text-[11px] text-slate-500 mt-1">{c.sub}</p>}
            </div>
          ))}
        </div>
      </div>
      {data.top_listings?.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-3">Öne çıkan ilanlarınız</p>
          <div className="space-y-2">
            {data.top_listings.map((l) => (
              <Link key={l.id} to={`/mezat/${l.id}`} className="block p-4 rounded-xl bg-white hover:bg-slate-50 transition-colors">
                <p className="font-medium text-slate-900">{l.title}</p>
                <div className="flex gap-4 text-xs text-slate-500 mt-1">
                  <span>{l.bid_count} teklif</span>
                  <span>{l.favorites_count} favori</span>
                  <span>Güncel: ₺{Number(l.current_price).toLocaleString("tr-TR")}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Seller payout summary card
function SellerPayoutCard() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/seller/payout-summary").then(({ data }) => setP(data)); }, []);
  if (!p) return null;
  const cards = [
    { label: "Toplam kazanç", value: `₺${Number(p.total_earned).toLocaleString("tr-TR")}`, tone: "" },
    { label: "Havuzda bekleyen", value: `₺${Number(p.in_escrow).toLocaleString("tr-TR")}`, tone: "text-amber-800" },
    { label: "Ödemesi bekleyen", value: `₺${Number(p.pending_payout).toLocaleString("tr-TR")}`, tone: "text-[#B54037]" },
    { label: "Ödendi", value: `₺${Number(p.paid_out).toLocaleString("tr-TR")}`, tone: "text-emerald-800" },
  ];
  return (
    <div data-testid="seller-payout-card">
      <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-3">Hakediş özeti</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="p-5 rounded-xl bg-white">
            <p className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-400 mb-2">{c.label}</p>
            <p className={`font-mono-tight text-xl font-medium tracking-tight ${c.tone || "text-slate-900"}`}>{c.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
